import { itemsApi } from "#/api/itemsApi";
import { productApi } from "#/api/productApi";
import PermissionTooltip from "#/components/ui/PermissionTooltip";
import { usePermission } from "#/hooks/usePermission";
import type { Item } from "#/types/items";
import { sanitizeItemStatusLabel } from "#/utils/StringUtils";
import { Button, Modal, Select, Text } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconAlertTriangle } from "@tabler/icons-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

interface BulkUpdateStatusModalProps {
  opened: boolean;
  onClose: () => void;
  /** The selected rows; their categories decide which statuses can be offered. */
  selectedItems: Pick<Item, "id" | "product">[];
  onSuccess: () => void;
}

/**
 * A status is always one of its category's scan steps, so a bulk change is
 * only possible when every selected item shares one category, and only to one
 * of that category's steps. The backend enforces the same rule.
 */
const BulkUpdateStatusModal = ({
  opened,
  onClose,
  selectedItems,
  onSuccess,
}: BulkUpdateStatusModalProps) => {
  const { t } = useTranslation();
  const { canWrite } = usePermission();
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);

  const categoryIds = useMemo(
    () => new Set(selectedItems.map((item) => item.product?.id ?? null)),
    [selectedItems],
  );
  const singleCategoryId =
    categoryIds.size === 1 ? ([...categoryIds][0] ?? null) : null;
  const mixedCategories = categoryIds.size > 1 || (categoryIds.size === 1 && singleCategoryId === null);

  const { data: steps, isPending: stepsPending } = useQuery({
    queryKey: ["product-statuses", singleCategoryId],
    queryFn: async () => {
      const response = await productApi.getStatuses(singleCategoryId as number);
      if (!response.data) throw new Error("Failed to fetch category scan steps");
      return [...response.data].sort((a, b) => a.step - b.step);
    },
    enabled: opened && singleCategoryId !== null,
    staleTime: 1000 * 60,
  });
  const stepsLoaded = singleCategoryId !== null && !stepsPending;
  const statusOptions = (steps ?? [])
    .filter((step) => !!step.label)
    .map((step) => ({ value: step.label, label: sanitizeItemStatusLabel(step.label) }));
  const categoryHasNoSteps = stepsLoaded && statusOptions.length === 0;
  const blocked = mixedCategories || singleCategoryId === null || categoryHasNoSteps;

  const bulkUpdateMutation = useMutation({
    mutationFn: async ({ itemIds, status }: { itemIds: number[]; status: string }) => {
      const response = await itemsApi.bulkUpdateStatus(itemIds, status);
      if (!response.data) {
        throw new Error("Failed to update items");
      }
      return response.data;
    },
    onSuccess: (data) => {
      notifications.show({
        title: t("common.success"),
        message: t("items.bulkUpdateStatus.success", { count: data.updated }),
        color: "green",
      });
      handleClose();
      onSuccess();
    },
    onError: (error: unknown) => {
      const errorMessage =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (error as Error)?.message ||
        t("items.bulkUpdateStatus.error");
      notifications.show({
        title: t("common.error"),
        message: errorMessage,
        color: "red",
      });
    },
  });

  const handleClose = () => {
    setSelectedStatus(null);
    onClose();
  };

  const handleSave = () => {
    if (selectedStatus && !blocked) {
      bulkUpdateMutation.mutate({
        itemIds: selectedItems.map((item) => item.id),
        status: selectedStatus,
      });
    }
  };

  const blockMessage = mixedCategories
    ? t("items.statusRules.bulkMixedCategories")
    : categoryHasNoSteps
      ? t("items.statusRules.bulkCategoryNoSteps")
      : null;

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      centered
      title={t("items.bulkUpdateStatus.title")}
      closeOnClickOutside={false}
      size="md"
      classNames={{
        title: "font-bold",
      }}
    >
      <div className="w-full flex flex-col gap-4">
        <Text size="sm" c="dimmed">
          {t("items.bulkUpdateStatus.description", { count: selectedItems.length })}
        </Text>

        {blockMessage && (
          <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            <IconAlertTriangle size={16} className="mt-0.5 shrink-0" />
            <span>{blockMessage}</span>
          </div>
        )}

        <Select
          label={t("common.status")}
          placeholder={t("items.bulkUpdateStatus.selectNewStatus")}
          searchable
          clearable
          withAsterisk
          disabled={blocked || (singleCategoryId !== null && stepsPending)}
          data={statusOptions}
          value={selectedStatus}
          onChange={setSelectedStatus}
        />

        <div className="flex items-center justify-end gap-2 pt-2">
          <Button
            variant="outline"
            color="black"
            onClick={handleClose}
            disabled={bulkUpdateMutation.isPending}
          >
            {t("common.cancel")}
          </Button>
          <PermissionTooltip disabled={!canWrite()}>
            <Button
              color="black"
              disabled={!selectedStatus || blocked || bulkUpdateMutation.isPending || !canWrite()}
              onClick={handleSave}
              loading={bulkUpdateMutation.isPending}
            >
              {t("common.save")}
            </Button>
          </PermissionTooltip>
        </div>
      </div>
    </Modal>
  );
};

export default BulkUpdateStatusModal;
